import React from "react";
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  Store,
  Wrench,
  Users,
  FlaskConical,
  ExternalLink,
} from "lucide-react";
import { aboutData } from "../data/mock";

const featureIcons = [Store, Users, Wrench, FlaskConical];

export const AboutSection = () => {
  return (
    <section
      id="about-us"
      className="py-24 lg:py-32 bg-[#F8F8F8] relative overflow-hidden"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/3 left-0 w-[500px] h-[500px] bg-[#F76B1C]/[0.03] rounded-full blur-[150px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 lg:mb-20">
          <span className="text-[#F76B1C] text-sm font-semibold uppercase tracking-[0.15em]">
            About 3D Printer Gear
          </span>
          <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold text-[#111111] leading-tight">
            {aboutData.headline}
          </h2>
          <p className="mt-6 text-lg text-[#666666] leading-relaxed">
            {aboutData.subheadline}
          </p>
        </div>

        {/* Features grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6 mb-16 lg:mb-20">
          {aboutData.features.map((feat, i) => {
            const IconComp = featureIcons[i];
            return (
              <div
                key={feat.title}
                className="group p-7 rounded-2xl bg-white border border-[#EAEAEA] hover:border-[#F76B1C]/25 hover:shadow-lg hover:shadow-[#F76B1C]/[0.04] transition-all duration-300 hover:-translate-y-1"
              >
                <div className="w-12 h-12 rounded-xl bg-[#F76B1C]/[0.08] flex items-center justify-center mb-5 group-hover:bg-[#F76B1C]/[0.15] transition-colors">
                  <IconComp size={22} className="text-[#F76B1C]" />
                </div>
                <h3 className="text-lg font-bold text-[#111111] mb-2">
                  {feat.title}
                </h3>
                <p className="text-sm text-[#888888] leading-relaxed">
                  {feat.description}
                </p>
              </div>
            );
          })}
        </div>

        {/* Contact + Map area */}
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Contact details */}
          <div className="rounded-2xl bg-white border border-[#EAEAEA] shadow-lg p-8 lg:p-10">
            <h3 className="text-2xl font-bold text-[#111111] mb-2">
              Visit Our Showroom
            </h3>
            <p className="text-[#888888] text-sm mb-8">
              Come see the full Formlabs lineup running live. No appointment
              needed during business hours.
            </p>

            <div className="space-y-5">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-[#F76B1C]/10 flex items-center justify-center shrink-0">
                  <MapPin size={18} className="text-[#F76B1C]" />
                </div>
                <div>
                  <p className="text-sm text-[#AAAAAA] mb-0.5">Address</p>
                  <p className="text-[#111111] font-medium">
                    {aboutData.contact.address}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-[#F76B1C]/10 flex items-center justify-center shrink-0">
                  <Phone size={18} className="text-[#F76B1C]" />
                </div>
                <div>
                  <p className="text-sm text-[#AAAAAA] mb-0.5">Phone</p>
                  <a
                    href={`tel:${aboutData.contact.phone}`}
                    className="text-[#111111] font-medium hover:text-[#F76B1C] transition-colors"
                  >
                    {aboutData.contact.phone}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-[#F76B1C]/10 flex items-center justify-center shrink-0">
                  <Mail size={18} className="text-[#F76B1C]" />
                </div>
                <div>
                  <p className="text-sm text-[#AAAAAA] mb-0.5">Email</p>
                  <a
                    href={`mailto:${aboutData.contact.email}`}
                    className="text-[#111111] font-medium hover:text-[#F76B1C] transition-colors"
                  >
                    {aboutData.contact.email}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-[#F76B1C]/10 flex items-center justify-center shrink-0">
                  <Clock size={18} className="text-[#F76B1C]" />
                </div>
                <div>
                  <p className="text-sm text-[#AAAAAA] mb-0.5">Opening Hours</p>
                  <p className="text-[#111111] font-medium">
                    {aboutData.contact.hours}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-3 mt-8 pt-6 border-t border-[#EEEEEE]">
              <a
                href={aboutData.contact.storeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#F76B1C] hover:bg-[#E05A0F] text-white font-semibold rounded-xl transition-colors duration-200 text-sm"
              >
                Visit Our Store
                <ExternalLink size={14} />
              </a>
              <a
                href={`mailto:${aboutData.contact.email}`}
                className="inline-flex items-center gap-2 px-6 py-3 border border-[#E0E0E0] hover:border-[#CCCCCC] text-[#555555] hover:text-[#111111] font-semibold rounded-xl transition-all duration-200 text-sm hover:bg-[#F5F5F5]"
              >
                Request a Sample
              </a>
            </div>
          </div>

          {/* Map embed */}
          <div className="rounded-2xl overflow-hidden border border-[#EAEAEA] shadow-lg min-h-[400px]">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3155.7!2d145.007!3d-37.717!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6ad644b01639e1e7%3A0x5045675218ccd30!2s781%20High%20St%2C%20Reservoir%20VIC%203073!5e0!3m2!1sen!2sau!4v1700000000000"
              width="100%"
              height="100%"
              style={{ border: 0, minHeight: "400px" }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="3D Printer Gear Location"
            />
          </div>
        </div>
      </div>
    </section>
  );
};
